'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowDown, Shield, Eye, Zap, ChevronRight, Play, Check, Globe, Lock, Bell, Layers } from 'lucide-react';
import { useLocale } from '@/lib/LocaleContext';

// ─── Animated number ticker ───────────────────────────────────────────────────
function NumberTicker({ from, to, suffix = '' }: { from: string; to: string; suffix?: string }) {
  const [current, setCurrent] = useState(from);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent(to);
        setAnimating(false);
      }, 400);
    }, 800);
    return () => clearTimeout(timer);
  }, [to]);

  return (
    <span className="inline-flex items-center gap-1 font-mono tabular-nums">
      <span
        className={`transition-all duration-400 ${animating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
        style={{ transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)' }}
      >
        {current}
      </span>
      {suffix && <span className="text-xs opacity-60">{suffix}</span>}
    </span>
  );
}

// ─── Change arrow animation ────────────────────────────────────────────────────
function ChangeArrow({ from, to, label, delay = 0 }: { from: string; to: string; label?: string; delay?: number }) {
  const [visible, setVisible] = useState(false);
  const [showTo, setShowTo] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setShowTo(true), delay + 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [delay]);

  return (
    <div className={`flex items-center gap-3 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <span className="font-mono text-lg font-semibold text-slate-400 line-through decoration-green-400">{from}</span>
      <span
        className="change-arrow text-xl font-bold transition-all duration-300"
        style={{ opacity: showTo ? 1 : 0.3 }}
      >
        →
      </span>
      <span
        className={`font-mono text-lg font-bold transition-all duration-500 ${showTo ? 'scale-105' : 'text-slate-300 scale-95'}`}
        style={{ transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)', color: showTo ? '#22c55e' : undefined }}
      >
        {to}
      </span>
      {label && (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 transition-all duration-300 ${showTo ? 'opacity-100' : 'opacity-0'}`}>
          {label}
        </span>
      )}
    </div>
  );
}

// ─── Animated signal card ──────────────────────────────────────────────────────
function AnimatedSignalCard({
  icon, title, subtitle, change, consequence, level, delay = 0
}: {
  icon: string; title: string; subtitle: string; change?: { from: string; to: string };
  consequence?: string; level: 'critical' | 'warning' | 'info'; delay?: number;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const levelStyles = {
    critical: 'border-green-400/50 bg-green-500/15',
    warning: 'border-teal-400/40 bg-teal-400/12',
    info: 'border-cyan-300/40 bg-cyan-300/10',
  };
  const dotStyles = {
    critical: 'bg-green-400',
    warning: 'bg-teal-400',
    info: 'bg-cyan-300',
  };

  return (
    <div
      ref={ref}
      className={`rounded-2xl border p-4 backdrop-blur-sm transition-all duration-700 ${levelStyles[level]} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms`, transitionTimingFunction: 'cubic-bezier(0.34,1.56,0.64,1)' }}
    >
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotStyles[level]}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">{icon}</span>
            <span className="font-semibold text-sm text-white/90">{title}</span>
          </div>
          <p className="text-xs text-white/65 mb-2">{subtitle}</p>
          {change && (
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-sm text-white/65 line-through">{change.from}</span>
              <span className="change-arrow font-bold">→</span>
              <span className="font-mono text-sm font-bold" style={{ color: '#22c55e' }}>{change.to}</span>
            </div>
          )}
          {consequence && (
            <p className="text-xs font-medium text-white/60 bg-white/5 rounded-lg px-2 py-1">{consequence}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Scroll-reveal wrapper ─────────────────────────────────────────────────────
function Reveal({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
      style={{ transitionDelay: `${delay}ms`, transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)' }}
    >
      {children}
    </div>
  );
}

// ─── Interactive demo tabs ─────────────────────────────────────────────────────
const demoScenarios = [
  {
    id: 'refund',
    label: '💰 Refund',
    title: 'Refund overdue',
    story: [
      { label: 'Amazon order #204-8821', type: 'source' },
      { label: 'Expected refund', from: '12 Aug', to: 'Still missing', type: 'change' },
      { label: '£84.99 not received', type: 'impact', severity: 'critical' },
      { label: 'Seller promised 5–7 working days', type: 'impact', severity: 'info' },
    ],
    conclusion: 'ChangedSince noticed what didn\'t happen.',
  },
  {
    id: 'bill',
    label: '⚡ Bills',
    title: 'Electricity bill increased',
    story: [
      { label: 'Energy provider · Monthly direct debit', type: 'source' },
      { label: 'Monthly amount', from: '£87/mo', to: '£104/mo', type: 'change' },
      { label: '+£204 per year', type: 'impact', severity: 'warning' },
      { label: 'Direct debit changes next cycle', type: 'impact', severity: 'info' },
    ],
    conclusion: 'ChangedSince calculated the annual impact.',
  },
  {
    id: 'invoice',
    label: '💼 Work',
    title: 'Client invoice overdue',
    story: [
      { label: 'Horizon Studio · Invoice #INV-2847', type: 'source' },
      { label: 'Payment status', from: 'Due 10 Aug', to: 'Overdue 3 days', type: 'change' },
      { label: '£1,200 not received', type: 'impact', severity: 'critical' },
      { label: 'Affects savings goal this month', type: 'impact', severity: 'warning' },
    ],
    conclusion: 'ChangedSince traced the consequence.',
  },
  {
    id: 'visa',
    label: '📄 Documents',
    title: 'Visa — action required',
    story: [
      { label: 'Home Office · Skilled Worker Visa', type: 'source' },
      { label: 'Application status', from: 'Under review', to: 'Documents needed', type: 'change' },
      { label: '10 working days to respond', type: 'conflict', severity: 'critical' },
      { label: 'Deadline: 27 August', type: 'impact', severity: 'warning' },
    ],
    conclusion: 'ChangedSince surfaced the deadline.',
  },
  {
    id: 'money',
    label: '💳 Subscription',
    title: 'Subscription increased',
    story: [
      { label: 'Netflix monthly plan', type: 'source' },
      { label: 'Monthly charge', from: '£10.99', to: '£12.99', type: 'change' },
      { label: '+£24.00 per year', type: 'impact', severity: 'warning' },
      { label: 'Renewal in 3 days', type: 'impact', severity: 'info' },
    ],
    conclusion: 'ChangedSince calculated the real cost.',
  },
  {
    id: 'travel',
    label: '✈️ Travel',
    title: 'Flight changed',
    story: [
      { label: 'Flight LHR → JFK', type: 'source' },
      { label: 'Departure time', from: '18:40', to: '20:10', type: 'change' },
      { label: 'Airport pickup still at 18:15', type: 'conflict', severity: 'critical' },
      { label: 'Dinner reservation at 21:00 may be affected', type: 'impact', severity: 'warning' },
    ],
    conclusion: 'ChangedSince caught the consequence.',
  },
];

function InteractiveDemo() {
  const [activeTab, setActiveTab] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const { localiseMoney } = useLocale();

  const handleTab = (idx: number) => {
    setActiveTab(idx);
    setAnimKey(k => k + 1);
  };

  const scenario = demoScenarios[activeTab];

  const severityStyles: Record<string, string> = {
    critical: 'bg-green-500/20 border-green-400/50 text-green-200',
    warning: 'bg-teal-400/20 border-teal-400/40 text-teal-200',
    info: 'bg-cyan-300/20 border-cyan-300/40 text-cyan-100',
  };

  return (
    <div className="rounded-3xl border border-white/15 shadow-2xl overflow-hidden" style={{ background: '#061020', boxShadow: '0 0 80px rgba(34,197,94,0.18), 0 20px 60px rgba(0,0,0,0.5)' }}>
      {/* Tab bar */}
      <div className="flex border-b border-white/10 bg-white/[0.03] overflow-x-auto scrollbar-hide">
        {demoScenarios.map((s, i) => (
          <button
            key={s.id}
            onClick={() => handleTab(i)}
            className={`flex-shrink-0 px-5 py-3.5 text-sm font-medium transition-all duration-200 border-b-2 ${
              activeTab === i
                ? 'border-green-400 text-green-200 bg-green-500/15' : 'border-transparent text-white/65 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Demo content */}
      <div className="p-6 sm:p-8 min-h-[320px]">
        <div key={animKey} className="space-y-4">
          <h3 className="font-bold text-xl text-white animate-slide-up">{scenario.title}</h3>

          {scenario.story.map((item, i) => (
            <div
              key={i}
              className={`transition-all duration-500 ${i === 0 ? 'opacity-100' : ''}`}
              style={{
                animation: 'slideUp 0.5s cubic-bezier(0.4,0,0.2,1) both',
                animationDelay: `${i * 120}ms`,
              } as React.CSSProperties}
            >
              {item.type === 'source' && (
                <p className="text-sm text-white/60 font-medium">{item.label}</p>
              )}
              {item.type === 'change' && (
                <div className="flex items-center gap-3 py-2">
                  <span className="text-xs text-white/65 w-28 flex-shrink-0">{item.label}</span>
                  <span className="font-mono font-semibold text-white/55 line-through">{localiseMoney(item.from ?? '')}</span>
                  <span className="change-arrow text-lg font-bold">→</span>
                  <span className="font-mono font-bold text-lg" style={{ color: '#22c55e' }}>{localiseMoney(item.to ?? '')}</span>
                </div>
              )}
              {(item.type === 'conflict' || item.type === 'impact') && item.severity && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium ${severityStyles[item.severity]}`}>
                  <span className="text-base">{item.type === 'conflict' ? '⚡' : '→'}</span>
                  {localiseMoney(item.label)}
                </div>
              )}
            </div>
          ))}

          <div
            className="mt-6 pt-4 border-t border-white/10"
            style={{ animation: 'slideUp 0.5s cubic-bezier(0.4,0,0.2,1) both', animationDelay: '600ms' } as React.CSSProperties}
          >
            <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#22c55e' }}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ background: 'rgba(34,197,94,0.25)', border: '1px solid rgba(34,197,94,0.5)' }}>✓</span>
              {scenario.conclusion}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── How it works steps ────────────────────────────────────────────────────────
const howItWorksSteps = [
  {
    step: '01',
    icon: '🔌',
    title: 'Connect what you choose',
    desc: 'Link only the services you want. Gmail, Calendar, bank — your call. ChangedSince never assumes permission.',
    color: 'from-green-600 to-teal-500',
  },
  {
    step: '02',
    icon: '🧠',
    title: 'ChangedSince learns what\'s true',
    desc: 'It builds a picture of your current life state — flight times, expected refunds, subscription amounts, meeting schedules.',
    color: 'from-teal-500 to-cyan-500',
  },
  {
    step: '03',
    icon: '🔍',
    title: 'Detects meaningful changes',
    desc: 'When something shifts, ChangedSince notices. Not every change — only the ones that could actually matter.',
    color: 'from-green-700 to-teal-500',
  },
  {
    step: '04',
    icon: '🕸️',
    title: 'Finds what else is affected',
    desc: 'A flight change isn\'t just a time update. ChangedSince checks your pickup, dinner, calendar — the whole chain.',
    color: 'from-teal-500 to-green-600',
  },
  {
    step: '05',
    icon: '💡',
    title: 'Explains why it matters',
    desc: 'Clear, human language. Not a data dump. ChangedSince tells you what changed, what it affects, and what you might want to do.',
    color: 'from-cyan-600 to-teal-500',
  },
  {
    step: '06',
    icon: '⚡',
    title: 'You decide what happens next',
    desc: 'ChangedSince surfaces options. You choose. It never acts without your permission.',
    color: 'from-green-600 to-cyan-500',
  },
];

// ─── Features bento ───────────────────────────────────────────────────────────
const features = [
  {
    icon: <Zap size={22} style={{ color: '#22c55e' }} />,
    title: 'Change Detection',
    desc: 'Catches the moment something shifts — price, time, status, amount.',
    size: 'col-span-1',
  },
  {
    icon: <Layers size={22} style={{ color: '#06b6d4' }} />,
    title: 'Impact Graph',
    desc: 'Maps what a change affects across your connected world.',
    size: 'col-span-1',
  },
  {
    icon: <Bell size={22} className="text-teal-400" />,
    title: 'Waiting For',
    desc: 'Notices when something should have happened but didn\'t. Refunds, replies, decisions, deliveries.',
    size: 'col-span-2',
  },
  {
    icon: <Eye size={22} style={{ color: '#22c55e' }} />,
    title: 'Personal Intelligence',
    desc: 'Learns what matters to you. The same change can be critical for one person and irrelevant for another.',
    size: 'col-span-2',
  },
  {
    icon: <Shield size={22} className="text-teal-500" />,
    title: 'Privacy first',
    desc: 'You control every permission. Disconnect any time.',
    size: 'col-span-1',
  },
  {
    icon: <Globe size={22} style={{ color: '#06b6d4' }} />,
    title: 'Global by design',
    desc: 'Works wherever you are. Adapts to your currency, date format, working week, and local services.',
    size: 'col-span-1',
  },
];

// ─── Main component ────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [showcaseVideoVisible, setShowcaseVideoVisible] = useState(false);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const { localiseMoney } = useLocale();

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Lazy-load the showcase video only when it enters the viewport
  useEffect(() => {
    const el = showcaseRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowcaseVideoVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#0a1628' }}>
      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/10" style={{ background: 'rgba(10,22,40,0.85)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/assets/images/9233ac2a-da42-49dd-8741-1bbcb2a05a7e-1786734258348.png"
              alt="ChangedSince logo"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg tracking-tight">
              <span className="text-white">Changed</span><span style={{ color: '#22c55e' }}>Since</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-sm text-white/70 hover:text-white transition-colors">How it works</a>
            <a href="#features" className="text-sm text-white/70 hover:text-white transition-colors">Features</a>
            <a href="#trust" className="text-sm text-white/70 hover:text-white transition-colors">Privacy</a>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login" className="text-sm font-medium text-white/80 hover:text-white transition-colors">
              Sign in
            </Link>
            <Link
              href="/sign-up-login"
              className="text-sm font-semibold px-4 py-2 rounded-xl text-white transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #22c55e, #06b6d4)', boxShadow: '0 4px 15px rgba(34,197,94,0.40)' }}
            >
              Get started
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero with animated gradient background ───── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-16 overflow-hidden">
        {/* CSS animated gradient background */}
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse 120% 80% at 50% 30%, rgba(34,197,94,0.18) 0%, rgba(10,22,40,0) 70%), #0a1628',
            }}
          />
          {/* Subtle slow-moving orb */}
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(6,182,212,0.14) 0%, transparent 70%)',
              animation: 'heroPulse 8s ease-in-out infinite',
            }}
          />
        </div>

        {/* Ambient glow layers */}
        <div className="absolute inset-0 pointer-events-none z-1">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(34,197,94,0.22) 0%, rgba(6,182,212,0.12) 40%, transparent 70%)' }} />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[400px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(6,182,212,0.10) 0%, transparent 70%)' }} />
          <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(34,197,94,0.12) 0%, transparent 70%)' }} />
          {/* Animated grid */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: 'linear-gradient(rgba(34,197,94,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.9) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
            style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.45)', color: '#22c55e' }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
            Personal change-intelligence
          </div>

          {/* Headline */}
          <h1
            className={`text-hero-xl font-extrabold text-white mb-6 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ textShadow: '0 0 80px rgba(34,197,94,0.35)' }}
          >
            Know what changed.
            <br />
            <span style={{ background: 'linear-gradient(90deg, #22c55e, #06b6d4, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Know what matters.
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={`text-lg sm:text-xl text-white/75 max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            ChangedSince keeps an eye on the things that matter to you — what you're waiting for, what you need, what you want, and what has changed.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <Link
              href="/sign-up-login"
              className="group flex items-center gap-2 px-7 py-3.5 rounded-2xl text-white font-semibold text-base transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #22c55e, #06b6d4)', boxShadow: '0 8px 30px rgba(34,197,94,0.45)' }}
            >
              Get Started
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#how-it-works"
              className="flex items-center gap-2 px-7 py-3.5 rounded-2xl border text-white/90 font-semibold text-base hover:text-white transition-all duration-200 bg-white/5 backdrop-blur-sm"
              style={{ borderColor: 'rgba(34,197,94,0.40)' }}
            >
              <Play size={14} className="fill-current" />
              See How It Works
            </a>
          </div>

          {/* Hero animated demo ─ change cards */}
          <div
            className={`relative max-w-3xl mx-auto transition-all duration-1000 delay-500 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
          >
            {/* Main change card */}
            <div className="bg-white/[0.07] backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl p-6 sm:p-8 text-left"
              style={{ boxShadow: '0 0 80px rgba(34,197,94,0.20), 0 20px 60px rgba(0,0,0,0.4)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-green-500/20 flex items-center justify-center text-lg">💰</div>
                <div>
                  <p className="font-bold text-white">Refund overdue</p>
                  <p className="text-xs text-white/55">Amazon · Order #205-8847 · 4 hours ago</p>
                </div>
                <span className="ml-auto px-2.5 py-1 rounded-full bg-green-500/20 text-green-200 text-xs font-semibold border border-green-400/50">High</span>
              </div>

              <div className="space-y-3 mb-5">
                <ChangeArrow from="Expected 12 Aug" to="Still missing" label="+1 day" delay={600} />
              </div>

              {/* Consequence chain */}
              <div className="space-y-2 pl-4 border-l-2 border-white/15">
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                  <span className="text-white/80">{localiseMoney('£84.99')} not received</span>
                  <span className="ml-auto text-xs font-medium text-green-300 bg-green-500/20 px-2 py-0.5 rounded-full border border-green-400/40">Overdue</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-300 flex-shrink-0" />
                  <span className="text-white/75">Seller promised 5–7 working days</span>
                  <span className="ml-auto text-xs font-medium text-teal-200 bg-teal-400/20 px-2 py-0.5 rounded-full border border-teal-300/40">Day 8</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 flex-shrink-0" />
                  <span className="text-white/60">Affects savings goal this month</span>
                  <span className="ml-auto text-xs font-medium text-white/65 bg-white/8 px-2 py-0.5 rounded-full border border-white/15">Linked</span>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                <p className="text-sm font-semibold" style={{ color: '#22c55e' }}>ChangedSince noticed what didn't happen.</p>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors" style={{ background: 'linear-gradient(135deg, #22c55e, #06b6d4)', boxShadow: '0 2px 12px rgba(34,197,94,0.40)' }}>Review</button>
                  <button className="px-3 py-1.5 rounded-lg border border-white/20 text-white/70 text-xs font-medium hover:bg-white/10 transition-colors">Why it matters</button>
                </div>
              </div>
            </div>

            {/* Floating secondary cards */}
            <div
              className="absolute -right-4 sm:-right-12 top-8 w-52 bg-white/[0.08] backdrop-blur-xl rounded-2xl border border-teal-400/30 shadow-xl p-4 hidden sm:block"
              style={{ animation: 'slideUp 0.6s cubic-bezier(0.34,1.56,0.64,1) 1.2s both', boxShadow: '0 0 40px rgba(6,182,212,0.18)' } as React.CSSProperties}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">⚡</span>
                <span className="text-xs font-semibold text-white/90">Electricity increased</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs text-white/65 line-through">{localiseMoney('£87/mo')}</span>
                <span className="change-arrow text-sm font-bold">→</span>
                <span className="font-mono text-xs font-bold" style={{ color: '#22c55e' }}>{localiseMoney('£104/mo')}</span>
              </div>
              <p className="text-xs text-teal-300 mt-1.5 font-medium">{localiseMoney('+£204/year')}</p>
            </div>

            <div
              className="absolute -left-4 sm:-left-12 bottom-8 w-48 bg-white/[0.08] backdrop-blur-xl rounded-2xl border border-green-400/30 shadow-xl p-4 hidden sm:block"
              style={{ animation: 'slideUp 0.6s cubic-bezier(0.34,1.56,0.64,1) 1.5s both', boxShadow: '0 0 40px rgba(34,197,94,0.12)' } as React.CSSProperties}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">💼</span>
                <span className="text-xs font-semibold text-white/90">Invoice overdue</span>
              </div>
              <p className="text-xs text-white/55">Due 10 Aug</p>
              <p className="text-xs font-bold text-green-300 mt-1">{localiseMoney('£1,200')} missing</p>
            </div>
          </div>

          {/* Scroll indicator */}
          <div
            className={`mt-16 flex flex-col items-center gap-2 text-white/65 transition-all duration-700 delay-700 ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <span className="text-xs font-medium">Scroll to explore</span>
            <ArrowDown size={16} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* ── Short showcase video section ─────────────────────────────────────── */}
      <section className="relative py-20 overflow-hidden" style={{ background: '#0a1628' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.10)' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#22c55e' }}>See it in action</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Your world, intelligently watched
              </h2>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div
              ref={showcaseRef}
              className="relative rounded-3xl overflow-hidden border border-white/15 shadow-2xl"
              style={{ boxShadow: '0 0 80px rgba(34,197,94,0.22), 0 40px 80px rgba(0,0,0,0.6)' }}
            >
              {/* Glow ring */}
              <div className="absolute -inset-px rounded-3xl pointer-events-none z-10" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.30) 0%, transparent 50%, rgba(6,182,212,0.20) 100%)' }} />
              {/* Placeholder shown until video lazy-loads */}
              {!showcaseVideoVisible ? (
                <div
                  className="w-full flex items-center justify-center"
                  style={{ height: '360px', background: 'linear-gradient(135deg, #061020, #0a1628)' }}
                >
                  <div className="flex flex-col items-center gap-3 text-white/60">
                    <Play size={40} className="opacity-50" />
                    <span className="text-sm">Loading preview…</span>
                  </div>
                </div>
              ) : (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto block"
                  style={{ maxHeight: '520px', objectFit: 'cover' }}
                >
                  <source src="https://assets.mixkit.co/videos/preview/mixkit-digital-animation-of-futuristic-devices-99786-large.mp4" type="video/mp4" />
                </video>
              )}
              {/* Bottom fade overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-10" style={{ background: 'linear-gradient(to top, #0a1628, transparent)' }} />
              {/* Corner badge */}
              <div className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-black/50 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
                <span className="text-white/80 text-xs font-medium">Live intelligence</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Cinematic dark section ───────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0a1628, #0d1f3c, #0a1628)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(34,197,94,0.10)' }} />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.08)' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-sm font-semibold tracking-widest uppercase mb-4" style={{ color: '#22c55e' }}>Life changes</p>
            <h2 className="text-display font-extrabold text-white mb-6">
              ChangedSince notices.
            </h2>
            <p className="text-white/65 text-lg max-w-xl mx-auto mb-12">
              While you sleep, travel, or focus on what matters — ChangedSince watches the services you choose and quietly builds a picture of your world.
            </p>
          </Reveal>

          {/* Cinematic stats row */}
          <Reveal delay={200}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
              {[
                { value: '5', label: 'things need your attention', suffix: '' },
                { value: '18', label: 'changes detected today', suffix: '' },
                { value: '£1,200', label: 'invoice overdue', suffix: '' },
                { value: '10', label: 'working days to respond', suffix: '' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/[0.07] border border-white/15 rounded-2xl p-4 text-center hover:bg-white/10 transition-colors">
                  <p className="text-2xl font-bold text-white font-mono tabular-nums">{stat.value}<span style={{ color: '#22c55e' }}>{stat.suffix}</span></p>
                  <p className="text-xs text-white/55 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* App preview panel */}
          <Reveal delay={300}>
            <div className="relative rounded-3xl overflow-hidden border border-white/15 shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #061020, #0d1f3c)', boxShadow: '0 0 100px rgba(34,197,94,0.15), 0 40px 80px rgba(0,0,0,0.5)' }}
            >
              {/* Animated background */}
              <div className="absolute inset-0">
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(6,182,212,0.08) 50%, rgba(13,31,60,0.9) 100%)' }} />
                {/* Animated signal rings */}
                <div className="absolute inset-0 flex items-center justify-center opacity-15">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute rounded-full"
                      style={{
                        width: `${(i + 1) * 120}px`,
                        height: `${(i + 1) * 120}px`,
                        border: '1px solid rgba(34,197,94,0.7)',
                        animation: `signal-pulse ${2 + i * 0.5}s ease-in-out infinite`,
                        animationDelay: `${i * 0.3}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Mock app UI */}
              <div className="relative z-10 p-8 sm:p-12">
                <div className="max-w-md mx-auto space-y-3">
                  {[
                    { icon: '💰', title: 'Refund overdue', badge: 'High', color: 'green' },
                    { icon: '⚡', title: 'Electricity bill increased', badge: 'High', color: 'green' },
                    { icon: '📄', title: 'Visa — documents needed', badge: 'Critical', color: 'teal' },
                    { icon: '💼', title: 'Invoice overdue', badge: 'High', color: 'green' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3 border border-white/15 hover:bg-white/15 transition-colors"
                      style={{ animation: `slideUp 0.5s ease both`, animationDelay: `${i * 200 + 500}ms` } as React.CSSProperties}
                    >
                      <span>{item.icon}</span>
                      <span className="text-white text-sm font-medium flex-1">{item.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        item.color === 'teal' ? 'bg-teal-600/30 text-teal-100 border border-teal-500/50' : 'bg-green-500/30 text-green-100 border border-green-400/50'
                      }`}>{item.badge}</span>
                    </div>
                  ))}
                  <div className="pt-3 text-center">
                    <p className="text-white/65 text-sm font-medium">Everything else looks normal.</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Interactive demo ─────────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0a1628, #0d1f3c, #0a1628)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.10)' }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#22c55e' }}>Live examples</p>
              <h2 className="text-display font-extrabold text-white mb-4">See ChangedSince in action</h2>
              <p className="text-white/65 text-lg max-w-xl mx-auto">
                Pick a scenario and watch ChangedSince detect the change, trace the consequences, and surface what matters.
              </p>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <InteractiveDemo />
          </Reveal>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: '#061020' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(34,197,94,0.07)' }} />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.07)' }} />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#22c55e' }}>The process</p>
              <h2 className="text-display font-extrabold text-white mb-4">How ChangedSince works</h2>
              <p className="text-white/65 text-lg max-w-xl mx-auto">
                Six steps from connection to clarity.
              </p>
            </div>
          </Reveal>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {howItWorksSteps.map((step, i) => (
              <Reveal key={step.step} delay={i * 80}>
                <div className={`group relative bg-white/[0.05] rounded-2xl border border-white/12 p-6 transition-all duration-300 ${i === 3 ? 'lg:col-span-2' : ''}`}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(34,197,94,0.15)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(34,197,94,0.40)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; (e.currentTarget as HTMLDivElement).style.borderColor = ''; (e.currentTarget as HTMLDivElement).style.transform = ''; }}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-lg mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {step.icon}
                  </div>
                  <div className="absolute top-4 right-4 font-mono text-xs font-bold text-white/20">{step.step}</div>
                  <h3 className="font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Flow indicator */}
          <Reveal delay={400}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-white/65">
              {['Connect', 'Understand', 'Detect', 'Connect consequences', 'Explain', 'Act'].map((step, i, arr) => (
                <React.Fragment key={step}>
                  <span className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)', color: '#22c55e' }}>{step}</span>
                  {i < arr.length - 1 && <ChevronRight size={14} style={{ color: 'rgba(34,197,94,0.60)' }} />}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Features bento ───────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0a1628, #0d1f3c, #0a1628)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[400px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.09)' }} />
          <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(34,197,94,0.08)' }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#22c55e' }}>Capabilities</p>
              <h2 className="text-display font-extrabold text-white mb-4">Built for your real life</h2>
              <p className="text-white/65 text-lg max-w-xl mx-auto">
                Not a dashboard. Not a chatbot. A quiet intelligence layer that watches what you choose.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 60}>
                <div
                  className={`group bg-white/[0.05] rounded-2xl border border-white/12 p-5 transition-all duration-300 ${f.size}`}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(34,197,94,0.15)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(34,197,94,0.40)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; (e.currentTarget as HTMLDivElement).style.borderColor = ''; (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.background = ''; }}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.30)' }}>
                    {f.icon}
                  </div>
                  <h3 className="font-bold text-white text-sm mb-1.5">{f.title}</h3>
                  <p className="text-xs text-white/55 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Signal examples row */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <AnimatedSignalCard
              icon="💰"
              title="Refund overdue"
              subtitle="Amazon · Expected 12 Aug"
              change={{ from: 'Expected', to: 'Still missing' }}
              consequence={localiseMoney('£84.99 not received')}
              level="warning"
              delay={0}
            />
            <AnimatedSignalCard
              icon="⚡"
              title="Electricity increased"
              subtitle="British Gas · Direct debit"
              change={{ from: localiseMoney('£87/mo'), to: localiseMoney('£104/mo') }}
              consequence={localiseMoney('+£204 per year')}
              level="warning"
              delay={150}
            />
            <AnimatedSignalCard
              icon="📄"
              title="Visa — action needed"
              subtitle="Home Office · Skilled Worker"
              change={{ from: 'Under review', to: 'Documents needed' }}
              consequence="10 working days to respond"
              level="critical"
              delay={300}
            />
          </div>
        </div>
      </section>

      {/* ── Trust / Privacy ───────────────────────────────────────────────────── */}
      <section id="trust" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #0a1628, #0d1f3c, #0a1628)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.09)' }} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(34,197,94,0.07)' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#22c55e' }}>Privacy</p>
              <h2 className="text-display font-extrabold text-white mb-4">Your world. Your permissions.</h2>
              <p className="text-white/65 text-lg max-w-xl mx-auto">
                ChangedSince handles sensitive personal information. Privacy isn't a policy — it's a product feature.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: permission visual */}
            <Reveal delay={100}>
              <div className="bg-white/[0.06] border border-white/15 rounded-3xl p-6 space-y-3"
                style={{ boxShadow: '0 0 50px rgba(34,197,94,0.08)' }}
              >
                <p className="text-white font-semibold mb-4 text-sm">Your World — 32% connected</p>
                <div className="w-full bg-white/10 rounded-full h-2 mb-6">
                  <div className="h-2 rounded-full transition-all duration-1000" style={{ width: '32%', background: 'linear-gradient(90deg, #22c55e, #06b6d4)' }} />
                </div>
                {[
                  { icon: '📧', name: 'Gmail', status: 'Connected', color: 'text-emerald-400' },
                  { icon: '📅', name: 'Google Calendar', status: 'Connected', color: 'text-emerald-400' },
                  { icon: '🏦', name: 'Bank account', status: 'Not connected', color: 'text-white/60' },
                  { icon: '📦', name: 'Amazon', status: 'Not connected', color: 'text-white/60' },
                  { icon: '✈️', name: 'Airlines', status: 'Not connected', color: 'text-white/60' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-white/8 last:border-0">
                    <span className="text-base">{item.icon}</span>
                    <span className="text-sm text-white/80 flex-1">{item.name}</span>
                    <span className={`text-xs font-medium ${item.color}`}>{item.status}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Right: trust points */}
            <Reveal delay={200}>
              <div className="space-y-6">
                {[
                  {
                    icon: <Lock size={18} style={{ color: '#22c55e' }} />,
                    title: 'You choose what ChangedSince can access',
                    desc: 'Signing in with Google does not give ChangedSince access to Gmail. Each permission is explicit and separate.',
                  },
                  {
                    icon: <Shield size={18} style={{ color: '#06b6d4' }} />,
                    title: 'Disconnect any time',
                    desc: 'Remove any connection instantly. ChangedSince stops reading that source immediately.',
                  },
                  {
                    icon: <Eye size={18} style={{ color: '#22c55e' }} />,
                    title: 'See exactly what ChangedSince knows',
                    desc: 'Your World shows every piece of information ChangedSince holds. Delete any of it at any time.',
                  },
                  {
                    icon: <Bell size={18} style={{ color: '#06b6d4' }} />,
                    title: 'Quiet by default',
                    desc: 'ChangedSince only surfaces what deserves your attention. Everything else stays silent.',
                  },
                ].map((point, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.30)' }}>
                      {point.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm mb-1">{point.title}</h3>
                      <p className="text-white/55 text-sm leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Social proof / quotes ─────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: '#061020' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] blur-3xl rounded-full" style={{ background: 'rgba(6,182,212,0.08)' }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="text-display font-extrabold text-white mb-4">Everything else looks normal.</h2>
              <p className="text-white/65 text-lg max-w-xl mx-auto">
                When ChangedSince has nothing important to surface, it stays quiet. That silence is the product.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                quote: '"I stopped checking my email every hour. ChangedSince tells me when something actually matters."',
                name: 'Frequent traveller',
                detail: 'Caught 3 flight changes last month',
              },
              {
                quote: '"It noticed my refund was overdue before I even remembered to check. That\'s the whole point."',
                name: 'Regular online shopper',
                detail: '£340 in refunds tracked',
              },
              {
                quote: '"The subscription price change alert saved me from a surprise charge. Simple, calm, useful."',
                name: 'Subscription manager',
                detail: '12 subscriptions monitored',
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 100}>
                <div
                  className="bg-white/[0.05] rounded-2xl border border-white/12 p-6 transition-all duration-300"
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(34,197,94,0.12)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(34,197,94,0.30)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; (e.currentTarget as HTMLDivElement).style.borderColor = ''; (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.background = ''; }}
                >
                  <p className="text-white/75 text-sm leading-relaxed mb-4 italic">{item.quote}</p>
                  <div className="pt-3 border-t border-white/10">
                    <p className="text-xs font-semibold text-white/90">{item.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#22c55e' }}>{item.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(to bottom, #0a1628, #061020)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="rounded-3xl p-10 sm:p-14 relative overflow-hidden border"
              style={{ background: 'linear-gradient(135deg, #0a1628, #0d1f3c, #061020)', borderColor: 'rgba(34,197,94,0.30)', boxShadow: '0 0 100px rgba(34,197,94,0.18), 0 40px 80px rgba(0,0,0,0.4)' }}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" style={{ background: 'rgba(34,197,94,0.15)' }} />
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl" style={{ background: 'rgba(6,182,212,0.12)' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[200px] rounded-full blur-3xl" style={{ background: 'rgba(34,197,94,0.10)' }} />
              </div>
              <div className="relative z-10">
                <p className="text-sm font-semibold tracking-widest uppercase mb-4" style={{ color: '#22c55e' }}>Get started</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
                  You don't need to keep checking everything.<br />Let ChangedSince watch.
                </h2>
                <p className="text-white/65 text-base mb-8 max-w-md mx-auto">
                  Connect what you choose. ChangedSince stays quiet until something deserves your attention.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    href="/sign-up-login"
                    className="group flex items-center gap-2 px-8 py-3.5 rounded-2xl text-white font-bold text-base transition-all duration-200 hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #22c55e, #06b6d4)', boxShadow: '0 8px 30px rgba(34,197,94,0.50)' }}
                  >
                    Get Started
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="mt-6 flex items-center justify-center gap-6 text-white/65 text-xs">
                  {['Free to start', 'No card required', 'Disconnect any time'].map((item, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <Check size={11} />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/10 py-10 px-4 sm:px-6 lg:px-8" style={{ background: '#0a1628' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #06b6d4)' }}>
              <span className="text-white font-bold text-xs">C</span>
            </div>
            <span className="font-bold text-sm text-white/90">ChangedSince</span>
            <span className="text-white/60 text-xs ml-2">Know what changed. Know what matters.</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-white/60">
            <Link href="/sign-up-login" className="hover:text-white/80 transition-colors">Sign in</Link>
            <a href="#trust" className="hover:text-white/80 transition-colors">Privacy</a>
            <a href="#how-it-works" className="hover:text-white/80 transition-colors">How it works</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
